/**
 * Coalescing runner for the queue's counts pass (decision D6).
 *
 * The three campaign counts used to refresh from three independent effects
 * AND from explicit calls after claim/release, so one claim fired each of
 * them twice: once eagerly, once again when the stream it had just changed
 * delivered. Eight aggregation queries became sixteen.
 *
 * The rule is: one triggering event causes one pass. A trigger arriving while
 * a pass is in flight does not start a second one — it marks the result
 * stale, and exactly one more pass runs when the first finishes, however many
 * triggers arrived meanwhile.
 */
export interface CoalescingRunner {
  /** Request a pass. Resolves when a pass that saw this request has finished. */
  request: () => Promise<void>;
  /** Stop scheduling further passes (component unmount). */
  cancel: () => void;
  /** Passes actually executed. Test seam. */
  runCount: () => number;
}

export function createCoalescingRunner(run: () => Promise<void>): CoalescingRunner {
  let inFlight: Promise<void> | null = null;
  let dirty = false;
  let cancelled = false;
  let runs = 0;

  async function drain(): Promise<void> {
    do {
      dirty = false;
      runs += 1;
      // eslint-disable-next-line no-await-in-loop
      await run();
    } while (dirty && !cancelled);
  }

  return {
    request() {
      if (cancelled) return Promise.resolve();
      if (inFlight) {
        dirty = true;
        return inFlight;
      }
      inFlight = drain().finally(() => { inFlight = null; });
      return inFlight;
    },
    cancel() {
      cancelled = true;
    },
    runCount() {
      return runs;
    },
  };
}
